export interface CharacterGroveImageRequirement {
  readonly key: string;
  readonly path: string;
  readonly width: number;
  readonly height: number;
  readonly alpha: boolean;
  readonly required: true;
}

const ROOT = 'assets/story-forest/character-grove';

const image = (
  key: string,
  path: string,
  width: number,
  height: number,
  alpha: boolean,
): CharacterGroveImageRequirement => ({ key, path: `${ROOT}/${path}`, width, height, alpha, required: true });

/** Authoritative runtime registry for the validated production pack. */
export const CHARACTER_GROVE_ASSETS = {
  background: image('character-grove-background', 'backgrounds/character-grove-bg.png', 1600, 900, false),
  grovePath: image('character-grove-path', 'environment/grove-path.png', 640, 480, true),
  foregroundLeft: image('character-grove-foreground-left', 'environment/foreground-left.png', 512, 720, true),
  foregroundRight: image('character-grove-foreground-right', 'environment/foreground-right.png', 512, 720, true),
  memoryTreeDormant: image('character-grove-memory-tree-dormant', 'memory-tree/memory-tree-dormant.png', 768, 1024, true),
  memoryTreeRestored: image('character-grove-memory-tree-restored', 'memory-tree/memory-tree-restored.png', 768, 1024, true),
  insightToken: image('character-grove-insight-token', 'interactives/character-insight-token.png', 384, 384, true),
  insightTokenSelected: image('character-grove-insight-token-selected', 'interactives/character-insight-token-selected.png', 384, 384, true),
  memoryEventToken: image('character-grove-memory-event-token', 'interactives/memory-event-token.png', 384, 384, true),
  evidenceLink: image('character-grove-evidence-link', 'interactives/evidence-link.png', 768, 256, true),
  evidenceSlot: image('character-grove-evidence-slot', 'interactives/evidence-slot.png', 384, 384, true),
  restoredBranch: image('character-grove-restored-branch', 'interactives/restored-branch.png', 768, 512, true),
  storyCharacterPanel: image('character-grove-story-character-panel', 'ui/story-character-panel.png', 960, 640, true),
  insightPanel: image('character-grove-insight-panel', 'ui/character-insight-panel.png', 640, 720, true),
  evidencePrompt: image('character-grove-evidence-prompt', 'ui/evidence-prompt.png', 880, 240, true),
  hintButton: image('character-grove-hint-button', 'ui/hint-button.png', 256, 128, true),
  resetButton: image('character-grove-reset-button', 'ui/reset-button.png', 256, 128, true),
  connectButton: image('character-grove-connect-button', 'ui/connect-button.png', 320, 144, true),
  memoryRevealVfx: image('character-grove-memory-reveal-vfx', 'vfx/memory-reveal.png', 1024, 1024, true),
  evidenceConnectionVfx: image('character-grove-evidence-connection-vfx', 'vfx/evidence-connection.png', 1024, 512, true),
  treeRestorationVfx: image('character-grove-tree-restoration-vfx', 'vfx/tree-restoration.png', 1024, 1024, true),
} as const;

export const CHARACTER_GROVE_ASSET_LIST = Object.values(CHARACTER_GROVE_ASSETS);

export const CHARACTER_GROVE_PRODUCTION_GATE = {
  routeEnabled: true,
  reason: 'production-assets-validated',
} as const;

export function preloadCharacterGroveAssets(scene: Phaser.Scene) {
  CHARACTER_GROVE_ASSET_LIST.forEach(asset => scene.load.image(asset.key, asset.path));
}

export function getMissingCharacterGroveAssets(textures: Phaser.Textures.TextureManager) {
  return CHARACTER_GROVE_ASSET_LIST.filter(asset => !textures.exists(asset.key));
}

export function canEnterCharacterGrove(textures: Phaser.Textures.TextureManager) {
  return CHARACTER_GROVE_PRODUCTION_GATE.routeEnabled
    && getMissingCharacterGroveAssets(textures).length === 0;
}
